import React, { RefObject, useMemo, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import { motion } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import { Row, TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { ForwardIcon } from '../../../icon/icons/forward';
import { IconButton } from '../../icon-button/icon-button.component';
import { AnimatedExpandable } from '../atoms/animated-expandable.container';
import { TableCommonStyles } from '../common-styles';
import { TableData } from '../table-data-type';
import { CellPositionCord, TableRowActions, TableExpandableRender, RowAction, CustomRowConfig } from '../table-types';
import { isReservedCellId } from '../utils/table-utils';

const tableRowRelativeStyle = css`
  position: relative;
`;

const tableRowGroupFlexStyle = css`
  display: flex;
`;

const tableRowLeftShiftStyle = css`
  margin-left: var(--table-row-left-shift);
`;

const tableRowCellGapStyle = css`
  gap: var(--table-row-cell-gap);
`;

const tableRowColGapStyle = css`
  column-gap: var(--table-row-col-gap);
`;

const tableRowRowGapStyle = css`
  row-gap: var(--table-row-row-gap);
`;

const rowHoverActionsDivStyle = css`
  width: 100%;
`;

const customRowSectionBaseStyle = css`
  display: flex;
  padding: ${theme.spacing(2)};
`;

const customRowSectionFlexStyle = css`
  flex: 1;
`;

const customRowSectionStickyStyle = css`
  position: sticky;
  left: 0;
`;
const mobileRowActionsMarginStyle = css`
  margin-bottom: ${theme.spacing(1)};
`;

const mobileRowActionsIconBtnAutoStyle = css`
  margin-right: auto;
`;

const mobileRowActionsMargin = css`
  margin-left: ${theme.spacing(1)};
`;

type TableRowProps<T extends TableData> = {
  row: Row<T>;
  rowIdx: number;
  rowCountPerPage: number;
  isFocusable?: boolean;
  tableId: string;
  currentCellPositionRef?: RefObject<CellPositionCord>;
  rowActions?: TableRowActions<T>;
  expandableRowComponent?: TableExpandableRender<T>;
  tableClientWidth: number;
  isLoading?: boolean;

  tableInstance: TableInstance<T>;
};

export const TableRow = <T extends TableData>({
  row,
  rowIdx,
  rowCountPerPage,
  isFocusable,
  tableId,
  currentCellPositionRef,
  rowActions,
  expandableRowComponent,
  tableClientWidth,
  isLoading,
  tableInstance,
}: TableRowProps<T>) => {
  const { isMobile, customRowConfig, leftShift, mobileLayoutGlobalConfig, rowExpandingConfig } = tableInstance;
  const rowCardStyles = TableCommonStyles.rowCardStyles;
  const rowCardContainerStyles = TableCommonStyles.rowCardContainerStyles;

  const [isOpen, setIsOpen] = useState(false);
  const customRowConditional =
    typeof customRowConfig?.condition === 'boolean'
      ? customRowConfig?.condition
      : customRowConfig?.condition?.(row.values, row.original, row);
  const handleRowClick = (e) => {
    if (isLoading || isMobile || !!row?.isDisabled) return;
    if (rowActions?.onRowClick) {
      rowActions.onRowClick(row.original, row, e);
      return;
    }
    if (!!expandableRowComponent && !isExpandableRowDisabled) {
      row.toggleRowExpanded();
    }
  };

  const isExpandableRowDisabled =
    typeof rowExpandingConfig?.isDisabled === 'boolean'
      ? rowExpandingConfig?.isDisabled
      : rowExpandingConfig?.isDisabled?.(row.original, row);

  const handleExpandRow = () => {
    if (!!expandableRowComponent) {
      row.toggleRowExpanded();
    }
  };

  return useMemo(() => {
    const { key, ...rowProps } = !isMobile ? row.getRowProps() : { key: '' };

    return (
      <>
        <div
          data-row-id={row.id}
          className={cx('table-row-group', tableRowGroupFlexStyle, isMobile && rowCardContainerStyles)}
        >
          {isMobile && (
            <MobileRowActions
              row={row}
              rowIdx={rowIdx}
              rowCountPerPage={rowCountPerPage}
              isFocusable={isFocusable}
              tableId={tableId}
              currentCellPositionRef={currentCellPositionRef}
              onRowClick={rowActions?.onRowClick}
              onRowSelect={rowActions?.onRowSelect}
              isExpandableRowDisabled={isExpandableRowDisabled}
              isLoading={isLoading}
              tableInstance={tableInstance}
              handleExpandRow={handleExpandRow}
              tableClientWidth={tableClientWidth}
            />
          )}
          <div
            key={key}
            {...rowProps}
            onClick={handleRowClick}
            className={cx(
              `table__row ${row.isDisabled || isExpandableRowDisabled ? 'disabled' : ''}`,
              isMobile && rowCardStyles,
              tableRowRelativeStyle,
              leftShift && tableRowLeftShiftStyle,
              mobileLayoutGlobalConfig?.cellGap && tableRowCellGapStyle,
              mobileLayoutGlobalConfig?.cellHorizontalGap && tableRowColGapStyle,
              mobileLayoutGlobalConfig?.cellVerticalGap && tableRowRowGapStyle
            )}
            role='row'
            aria-selected={row.isSelected}
            aria-expanded={row.isExpanded}
            onMouseEnter={() => {
              if (rowActions?.shouldHover && !isMobile) setIsOpen(true);
            }}
            onMouseLeave={() => {
              if (rowActions?.shouldHover && !isMobile) setIsOpen(false);
            }}
            style={
              {
                ...rowProps.style,
                ...(leftShift ? ({ '--table-row-left-shift': `${leftShift}px` } as any) : undefined),
                ...(mobileLayoutGlobalConfig?.cellGap
                  ? ({ '--table-row-cell-gap': mobileLayoutGlobalConfig.cellGap } as any)
                  : undefined),
                ...(mobileLayoutGlobalConfig?.cellHorizontalGap
                  ? ({ '--table-row-col-gap': mobileLayoutGlobalConfig?.cellHorizontalGap } as any)
                  : undefined),
                ...(mobileLayoutGlobalConfig?.cellVerticalGap
                  ? ({ '--table-row-row-gap': mobileLayoutGlobalConfig?.cellVerticalGap } as any)
                  : undefined),
              } as any
            }
          >
            {!customRowConditional &&
              row.cells.map((cell, idx) => {
                const isReservedCell = isReservedCellId(cell.column.id);
                const cellPosition: CellPositionCord = {
                  tableId,
                  x: idx,
                  y: rowIdx % rowCountPerPage,
                };

                if (isMobile && isReservedCell) return null;
                return (
                  <React.Fragment key={`row-${row.id}-cell-${cell.column.id}`}>
                    {cell.render('Cell', {
                      cellPosition,
                      currentCellPositionRef,
                      isFocusable,
                      onRowClick: rowActions?.onRowClick,
                      onRowSelect: rowActions?.onRowSelect,
                      hasExpandableRow: !!expandableRowComponent,
                      isExpandableRowDisabled,
                      isLoading,
                      tableId,
                      handleExpandRow,
                      tableClientWidth,
                      tableInstance,
                      cellTrackingId: cell.column.cellTrackingId,
                    })}
                  </React.Fragment>
                );
              })}
            {isOpen && !isLoading && rowActions?.shouldHover && !isMobile && (
              <RowHoverActions rowActions={rowActions} row={row} />
            )}
          </div>
        </div>
        {customRowConditional && <CustomRowComponent customRowConfig={customRowConfig} row={row} />}
        {!!expandableRowComponent && (
          <AnimatedExpandable isActive={row.isExpanded}>
            {expandableRowComponent(row.original, row.values, tableInstance)}
          </AnimatedExpandable>
        )}
      </>
    );
  }, [
    row,
    rowIdx,
    rowCountPerPage,
    isFocusable,
    tableId,
    currentCellPositionRef,
    rowActions?.onRowClick,
    row.isSelected,
    row.isExpanded,
    row.isDisabled,
    tableClientWidth,
    isOpen,
    rowActions?.actions,
    tableInstance?.state?.sortBy,
    tableInstance?.visibleColumns,
    customRowConditional,
    customRowConfig,
    isExpandableRowDisabled,
  ]);
};

type MobileRowActions<T extends TableData> = {
  row: Row<T>;
  rowIdx: number;
  rowCountPerPage: number;
  isFocusable?: boolean;
  tableId: string;
  currentCellPositionRef?: RefObject<CellPositionCord>;
  onRowClick?: TableRowActions<T>['onRowClick'];
  onRowSelect?: TableRowActions<T>['onRowSelect'];
  isLoading?: boolean;
  tableClientWidth: number;
  tableInstance: TableInstance<T>;
  handleExpandRow: () => void;
  isExpandableRowDisabled: boolean | undefined;
};

const MobileRowActions = <T extends TableData>({
  row,
  rowIdx,
  rowCountPerPage,
  isFocusable,
  tableId,
  currentCellPositionRef,
  onRowClick,
  onRowSelect,
  isLoading,
  tableClientWidth,
  tableInstance,
  handleExpandRow,
  isExpandableRowDisabled,
}: MobileRowActions<T>) => {
  const mobileRowActionsStyles = TableCommonStyles.mobileRowActionsStyles({
    isExpanded: row.isExpanded,
    isSelected: row.isSelected,
  });
  const allRowActions = row.cells.filter((cell) => isReservedCellId(cell.column.id));

  if (!allRowActions.length && !onRowClick) return null;

  return (
    <div css={mobileRowActionsStyles} className={mobileRowActionsMarginStyle}>
      {onRowClick && (
        <IconButton
          className={cx(!tableInstance.isSelectable && mobileRowActionsIconBtnAutoStyle, mobileRowActionsMargin)}
          label='Row Click'
          onClick={(e) => onRowClick(row.original, row, e)}
        >
          <ForwardIcon color='light' />
        </IconButton>
      )}
      {allRowActions.map((cell, idx) => {
        const cellPosition: CellPositionCord = {
          tableId,
          x: idx,
          y: rowIdx % rowCountPerPage,
        };
        return (
          <React.Fragment key={`row-${row.id}-cell-${cell.column.id}`}>
            {cell.render('Cell', {
              cellPosition,
              currentCellPositionRef,
              isFocusable,
              onRowClick,
              onRowSelect,
              isLoading,
              tableId,
              isExpandableRowDisabled,
              handleExpandRow,
              tableClientWidth,
              tableInstance,
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};

type RowHoverActions<T extends TableData> = {
  rowActions?: TableRowActions<T>;
  row: Row<T>;
};

const RowHoverActions = <T extends TableData>({ rowActions, row }: RowHoverActions<T>) => {
  const hoverActionsStyles = TableCommonStyles.hoverRowActionsStyles;

  const hiddenActions = () => {
    if (rowActions?.actions) {
      return rowActions?.actions?.reduce((acc, { hide }) => {
        if (typeof hide !== 'function' ? hide : hide(row?.original)) {
          return acc + 1;
        }
        return acc;
      }, 0);
    }
    return 0;
  };

  if (hiddenActions() === rowActions?.actions?.length) return null;

  type TODO = any;

  return (
    <div className='table__hover__actions'>
      <div className={rowHoverActionsDivStyle}></div>
      <motion.div
        role='dialog'
        layoutId='table__hover__actions'
        onClick={(e) => e.stopPropagation()}
        transition={{ duration: 0.2 }}
        className={hoverActionsStyles}
      >
        {rowActions?.actions?.map(({ onClick, Icon, ...rest }, id) => {
          const { hide, ...dynamicProps } = Object.entries(rest).reduce(
            (acc, [key, value]) => ({
              ...acc,
              ...(key !== 'onClick'
                ? { [key]: typeof value !== 'function' ? value : (value as TODO)(row.original) }
                : {}),
            }),
            {} as Omit<RowAction<T>, 'onClick' | 'Icon'>
          );

          return (
            !hide && (
              <IconButton
                key={id}
                onClick={() => {
                  onClick?.(row.original, row);
                }}
                {...dynamicProps}
                showLabelOnHover
                label={onlyText(dynamicProps.label as React.ReactNode)}
              >
                {Icon && <Icon />}
              </IconButton>
            )
          );
        })}
      </motion.div>
    </div>
  );
};

export const CustomRowComponent = <T extends TableData>({
  customRowConfig,
  row,
}: {
  customRowConfig?: CustomRowConfig<T>;
  row: Row<T>;
}) => {
  const { render, isSticky, wrapperStyle } = customRowConfig ?? {};

  return (
    <article className='custom__row__container' style={{ display: 'flex' }}>
      <section
        className={cx(
          'custom__row__sticky',
          customRowSectionBaseStyle,
          !isSticky && customRowSectionFlexStyle,
          isSticky && customRowSectionStickyStyle
        )}
        css={wrapperStyle as any}
      >
        {render?.(row['values'], row['original'], row)}
      </section>
    </article>
  );
};
